import { createStore, get, set } from 'idb-keyval';
import { customAlphabet, urlAlphabet } from 'nanoid';

// 使用 urlAlphabet 并过滤掉 - 和 _ 字符
const nanoid = customAlphabet( urlAlphabet.replace( /-|_/g, '' ));

// 创建独立的 IndexedDB store 用于存储 session id
// 主线程和 Service Worker 都可以访问同一个 IndexedDB
const sessionStore = createStore( 'error-uploader-session-db', 'session-store' );

// Session ID 相关的 key
const SESSION_FIXED_PART_KEY = 'session-fixed-part';
const SESSION_RANDOM_PART_KEY = 'session-random-part';

// 用于防止并发竞态条件的 Promise 锁
let fixedPartPromise: Promise<string> | null = null;

/**
 * 获取或生成固定部分（首次生成后永久存储）
 * 使用 Promise 锁避免并发调用时生成多个不同的 fixedPart
 */
export async function getOrCreateFixedPart(): Promise<string> {

    if ( fixedPartPromise ) {

        return fixedPartPromise;
    
    }

    fixedPartPromise = ( async () => {

        let fixedPart = await get<string>( SESSION_FIXED_PART_KEY, sessionStore );
        if ( !fixedPart ) {

            fixedPart = nanoid( 8 );
            await set( SESSION_FIXED_PART_KEY, fixedPart, sessionStore );
        
        }
        return fixedPart;
    
    })();

    return fixedPartPromise;

}

/**
 * 生成新的随机部分并存储（每次 session 都会重新生成）
 */
export async function generateAndStoreRandomPart(): Promise<string> {

    const randomPart = nanoid( 8 );
    await set( SESSION_RANDOM_PART_KEY, randomPart, sessionStore );
    return randomPart;

}

/**
 * 初始化 session id，格式: fixedPart-randomPart
 * 前半部分固定，后半部分每次 session 随机生成
 */
export async function initSessionId(): Promise<string> {

    const fixedPart = await getOrCreateFixedPart();
    const randomPart = await generateAndStoreRandomPart();
    return `${fixedPart}-${randomPart}`;

}

/**
 * 从 IndexedDB 获取当前存储的 session id
 * 适用于 Service Worker 等需要读取已存储 session id 的场景
 */
export async function getSessionIdFromStore(): Promise<string | null> {

    await initSessionId();

    const fixedPart = await get<string>( SESSION_FIXED_PART_KEY, sessionStore );
    const randomPart = await get<string>( SESSION_RANDOM_PART_KEY, sessionStore );
    if ( fixedPart && randomPart ) {

        return `${fixedPart}-${randomPart}`;
    
    }
    return null;

}
